import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getOrganizations, createOrganization, updateOrganization, toggleFoundingMember,
  getCertifications, getExpiringCertifications, createCertification, renewCertification, revokeCertification,
  getStandards, getTools, getUsers, createUser, deleteUser, downloadCertificatePdf,
  getAuditLogs, resetPassword, getMe,
  getPendingApplications, approveApplication, denyApplication, exportRegistryCSV,
  getAdminAssessments, getAssessmentStats
} from '../lib/api';
import { isAdmin, clearAuth, getUserName, getUser, setAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { downloadBlob } from '../lib/utils';
import { 
  Building2, Award, FileText, Wrench, Users, LogOut, 
  Clock, Shield, AlertTriangle, ClipboardList, BarChart3, Sparkles
} from 'lucide-react';

// Admin sub-components
import {
  AdminApplications,
  AdminOrganizations,
  AdminDeclarations,
  AdminExpiring,
  AdminStandards,
  AdminTools,
  AdminUsers,
  AdminAuditLog,
  AdminAssessments,
  FunnelAnalytics,
  OrgModal,
  CertModal,
  UserModal,
  RevokeModal,
  DenyModal,
  PasswordModal
} from '../components/admin';
import AxiomAnalytics from '../components/AxiomAnalytics';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('organizations');
  
  // Data state
  const [organizations, setOrganizations] = useState([]);
  const [declarations, setDeclarations] = useState([]);
  const [expiringCerts, setExpiringCerts] = useState([]);
  const [pendingApps, setPendingApps] = useState([]);
  const [standards, setStandards] = useState([]);
  const [tools, setTools] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [assessmentStats, setAssessmentStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [showCertModal, setShowCertModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDenyModal, setShowDenyModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [revokingCert, setRevokingCert] = useState(null);
  const [denyingApp, setDenyingApp] = useState(null);
  const [denyReason, setDenyReason] = useState('');
  
  // Form state
  const [orgForm, setOrgForm] = useState({ legal_name: '', display_name: '', website: '', contact_email: '', status: 'active', founding_member: false });
  const [certForm, setCertForm] = useState({ org_id: '', standard_id: '', issue_date: '', expiry_date: '', notes: '' });
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'org_user', org_id: '' });
  const [revokeReason, setRevokeReason] = useState('');
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  
  // Check if must reset password
  const [mustResetPassword, setMustResetPassword] = useState(false);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/admin/login');
      return;
    }
    const user = getUser();
    if (user?.must_reset_password) {
      setMustResetPassword(true);
      setShowPasswordModal(true);
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [orgsRes, certsRes, expiringRes, pendingRes, standardsRes, toolsRes, usersRes, auditRes, assessmentsRes, assessmentStatsRes] = await Promise.all([
        getOrganizations(),
        getCertifications(),
        getExpiringCertifications(),
        getPendingApplications(),
        getStandards(),
        getTools(),
        getUsers(),
        getAuditLogs(50),
        getAdminAssessments(1, 100),
        getAssessmentStats()
      ]);
      setOrganizations(orgsRes.data);
      setDeclarations(certsRes.data);
      setExpiringCerts(expiringRes.data);
      setPendingApps(pendingRes.data);
      setStandards(standardsRes.data);
      setTools(toolsRes.data);
      setUsers(usersRes.data);
      setAuditLogs(auditRes.data);
      setAssessments(assessmentsRes.data.assessments || []);
      setAssessmentStats(assessmentStatsRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/admin/login');
  };

  const handlePasswordReset = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 12) {
      alert('Password must be at least 12 characters');
      return;
    }
    try {
      await resetPassword(passwordForm.currentPassword, passwordForm.newPassword);
      const meRes = await getMe();
      const token = localStorage.getItem('token');
      setAuth(token, meRes.data);
      setMustResetPassword(false);
      setShowPasswordModal(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      alert('Password reset successfully');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  // Organization handlers
  const handleCreateOrg = async () => {
    try {
      if (editingOrg) {
        await updateOrganization(editingOrg.id, orgForm);
      } else {
        await createOrganization(orgForm);
      }
      setShowOrgModal(false);
      setOrgForm({ legal_name: '', display_name: '', website: '', contact_email: '', status: 'active', founding_member: false });
      setEditingOrg(null);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save organization');
    }
  };

  const openEditOrg = (org) => {
    setEditingOrg(org);
    setOrgForm({
      legal_name: org.legal_name,
      display_name: org.display_name,
      website: org.website || '',
      contact_email: org.contact_email,
      status: org.status,
      founding_member: org.founding_member || false
    });
    setShowOrgModal(true);
  };

  const handleToggleFoundingMember = async (orgId, currentStatus) => {
    try {
      await toggleFoundingMember(orgId, !currentStatus);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update founding member status');
    }
  };

  // Declaration handlers
  const handleCreateCert = async () => {
    try {
      await createCertification({
        ...certForm,
        issue_date: certForm.issue_date,
        expiry_date: certForm.expiry_date
      });
      setShowCertModal(false);
      setCertForm({ org_id: '', standard_id: '', issue_date: '', expiry_date: '', notes: '' });
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to issue declaration');
    }
  };

  const handleRenew = async (certId) => {
    if (!window.confirm('Renew this declaration? New dates will be set.')) return;
    try {
      await renewCertification(certId);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to renew declaration');
    }
  };

  const openRevokeModal = (cert) => {
    setRevokingCert(cert);
    setRevokeReason('');
    setShowRevokeModal(true);
  };

  const handleRevoke = async () => {
    if (!revokeReason.trim()) {
      alert('Please provide a reason for revocation');
      return;
    }
    try {
      await revokeCertification(revokingCert.id, revokeReason);
      setShowRevokeModal(false);
      setRevokingCert(null);
      setRevokeReason('');
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to revoke declaration');
    }
  };

  const handleDownloadPdf = async (certId, declarationId) => {
    try {
      const response = await downloadCertificatePdf(certId);
      downloadBlob(response.data, `certificate-${declarationId}.pdf`);
    } catch (err) {
      alert('Failed to download certificate');
    }
  };

  const handleExportRegistryCSV = async () => {
    try {
      const response = await exportRegistryCSV();
      const today = new Date().toISOString().split('T')[0];
      downloadBlob(response.data, `registry_export_${today}.csv`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to export registry');
    }
  };

  // User handlers
  const handleCreateUser = async () => {
    try {
      await createUser(userForm);
      setShowUserModal(false);
      setUserForm({ name: '', email: '', password: '', role: 'org_user', org_id: '' });
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await deleteUser(userId);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Application handlers
  const handleApprove = async (orgId) => {
    if (!window.confirm('Approve this application? This will issue declarations and add the organization to the public registry.')) return;
    try {
      await approveApplication(orgId);
      alert('Application approved successfully');
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to approve application');
    }
  };

  const openDenyModal = (app) => {
    setDenyingApp(app);
    setDenyReason('');
    setShowDenyModal(true);
  };

  const handleDeny = async () => {
    if (!denyReason || denyReason.trim().length < 10) {
      alert('Please provide a denial reason (at least 10 characters)');
      return;
    }
    try {
      await denyApplication(denyingApp.organization.id, denyReason);
      setShowDenyModal(false);
      setDenyingApp(null);
      setDenyReason('');
      alert('Application denied and refund initiated');
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to deny application');
    }
  };

  const openCertModal = () => {
    const today = new Date().toISOString().split('T')[0];
    const nextYear = new Date();
    nextYear.setFullYear(nextYear.getFullYear() + 1);
    const expiryDate = nextYear.toISOString().split('T')[0];
    setCertForm({ ...certForm, issue_date: today, expiry_date: expiryDate });
    setShowCertModal(true);
  };

  const openCreateOrg = () => {
    setEditingOrg(null);
    setOrgForm({ legal_name: '', display_name: '', website: '', contact_email: '', status: 'active', founding_member: false });
    setShowOrgModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-zinc-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="admin-dashboard">
      {/* Header */}
      <header className="header-bar">
        <div className="flex items-center gap-4">
          <span className="header-logo">AIAS Admin</span>
          <span className="text-zinc-300">|</span>
          <span className="text-sm text-zinc-500">{getUserName()}</span>
        </div>
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setShowPasswordModal(true)} className="text-zinc-600" data-testid="change-password-btn">
            <Shield className="w-4 h-4 mr-2" />
            Security
          </Button>
          <Button variant="ghost" onClick={handleLogout} className="text-zinc-600" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Pending Applications Alert */}
      {pendingApps.length > 0 && (
        <div className="bg-blue-50 border-b border-blue-200 px-8 py-3">
          <div className="flex items-center gap-2 text-blue-800">
            <ClipboardList className="w-4 h-4" />
            <span className="text-sm font-medium">{pendingApps.length} application(s) pending review</span>
            <Button variant="ghost" size="sm" onClick={() => setActiveTab('applications')} className="text-blue-700 underline">
              Review
            </Button>
          </div>
        </div>
      )}

      {/* Expiring Declarations Alert */}
      {expiringCerts.length > 0 && (
        <div className="bg-amber-50 border-b border-amber-200 px-8 py-3">
          <div className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm font-medium">{expiringCerts.length} declaration(s) expiring in the next 30 days</span>
            <Button variant="ghost" size="sm" onClick={() => setActiveTab('expiring')} className="text-amber-700 underline">
              View
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="p-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-10 h-12 rounded-none border border-zinc-200 bg-zinc-50 p-1">
            <TabsTrigger value="applications" className="rounded-none data-[state=active]:bg-white relative" data-testid="tab-applications">
              <ClipboardList className="w-4 h-4 mr-2" />
              Apps
              {pendingApps.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
                  {pendingApps.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="organizations" className="rounded-none data-[state=active]:bg-white" data-testid="tab-organizations">
              <Building2 className="w-4 h-4 mr-2" />
              Orgs
            </TabsTrigger>
            <TabsTrigger value="declarations" className="rounded-none data-[state=active]:bg-white" data-testid="tab-declarations">
              <Award className="w-4 h-4 mr-2" />
              Certs
            </TabsTrigger>
            <TabsTrigger value="expiring" className="rounded-none data-[state=active]:bg-white relative" data-testid="tab-expiring">
              <Clock className="w-4 h-4 mr-2" />
              Exp
              {expiringCerts.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center">
                  {expiringCerts.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="standards" className="rounded-none data-[state=active]:bg-white" data-testid="tab-standards">
              <FileText className="w-4 h-4 mr-2" />
              Std
            </TabsTrigger>
            <TabsTrigger value="tools" className="rounded-none data-[state=active]:bg-white" data-testid="tab-tools">
              <Wrench className="w-4 h-4 mr-2" />
              Tools
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-none data-[state=active]:bg-white" data-testid="tab-users">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="audit" className="rounded-none data-[state=active]:bg-white" data-testid="tab-audit">
              <Shield className="w-4 h-4 mr-2" />
              Audit
            </TabsTrigger>
            <TabsTrigger value="assessments" className="rounded-none data-[state=active]:bg-white" data-testid="tab-assessments">
              <BarChart3 className="w-4 h-4 mr-2" />
              Score
            </TabsTrigger>
            <TabsTrigger value="axiom" className="rounded-none data-[state=active]:bg-white" data-testid="tab-axiom">
              <Sparkles className="w-4 h-4 mr-2" />
              AI
            </TabsTrigger>
          </TabsList>

          <TabsContent value="applications" className="mt-6">
            <AdminApplications 
              pendingApps={pendingApps}
              onApprove={handleApprove}
              onDeny={openDenyModal}
              onRefresh={loadData}
            />
          </TabsContent>

          <TabsContent value="organizations" className="mt-6">
            <AdminOrganizations 
              organizations={organizations}
              onCreateOrg={openCreateOrg}
              onEditOrg={openEditOrg}
              onToggleFoundingMember={handleToggleFoundingMember}
              onExportCSV={handleExportRegistryCSV}
            />
          </TabsContent>

          <TabsContent value="declarations" className="mt-6">
            <AdminDeclarations 
              declarations={declarations}
              onOpenCertModal={openCertModal}
              onDownloadPdf={handleDownloadPdf}
              onRenew={handleRenew}
              onRevoke={openRevokeModal}
            />
          </TabsContent>

          <TabsContent value="expiring" className="mt-6">
            <AdminExpiring 
              expiringCerts={expiringCerts}
              onRenew={handleRenew}
            />
          </TabsContent>

          <TabsContent value="standards" className="mt-6">
            <AdminStandards standards={standards} />
          </TabsContent>

          <TabsContent value="tools" className="mt-6">
            <AdminTools tools={tools} />
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <AdminUsers 
              users={users}
              organizations={organizations}
              onCreateUser={() => setShowUserModal(true)}
              onDeleteUser={handleDeleteUser}
            />
          </TabsContent>

          <TabsContent value="audit" className="mt-6">
            <AdminAuditLog auditLogs={auditLogs} />
          </TabsContent>

          <TabsContent value="assessments" className="mt-6">
            <AdminAssessments 
              assessments={assessments}
              assessmentStats={assessmentStats}
              onRefresh={loadData}
            />
          </TabsContent>

          <TabsContent value="axiom" className="mt-6">
            <div className="space-y-8">
              <FunnelAnalytics />
              <AxiomAnalytics />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modals */}
      <OrgModal 
        open={showOrgModal}
        onOpenChange={setShowOrgModal}
        orgForm={orgForm}
        setOrgForm={setOrgForm}
        editingOrg={editingOrg}
        onSave={handleCreateOrg}
      />

      <CertModal 
        open={showCertModal}
        onOpenChange={setShowCertModal}
        certForm={certForm}
        setCertForm={setCertForm}
        organizations={organizations}
        standards={standards}
        onSave={handleCreateCert}
      />

      <UserModal 
        open={showUserModal}
        onOpenChange={setShowUserModal}
        userForm={userForm}
        setUserForm={setUserForm}
        organizations={organizations}
        onSave={handleCreateUser}
      />

      <RevokeModal 
        open={showRevokeModal}
        onOpenChange={setShowRevokeModal}
        revokingCert={revokingCert}
        revokeReason={revokeReason}
        setRevokeReason={setRevokeReason}
        onRevoke={handleRevoke}
      />

      <DenyModal 
        open={showDenyModal}
        onOpenChange={setShowDenyModal}
        denyingApp={denyingApp}
        denyReason={denyReason}
        setDenyReason={setDenyReason}
        onDeny={handleDeny}
      />

      <PasswordModal 
        open={showPasswordModal}
        onOpenChange={setShowPasswordModal}
        mustResetPassword={mustResetPassword}
        passwordForm={passwordForm}
        setPasswordForm={setPasswordForm}
        onReset={handlePasswordReset}
      />
    </div>
  );
}
